/**
 * `<ReadOnlyModal>` — opinionated MUI Dialog wrapper that hard-codes
 * the "read-only edit modal" pattern Christian locked in the PR #58
 * walkthrough (transcript [00:02:09] / [00:04:55] / [00:05:24] /
 * [00:05:48] / [00:06:09]).
 *
 * When `readOnly === true`:
 *   - A sticky `role="status"` banner is rendered at the top of the
 *     dialog explaining why the user can't edit. The banner is
 *     `aria-live="polite"` so screen-reader users hear the gate when
 *     the modal opens.
 *   - The Save / Submit footer slot is suppressed entirely (not just
 *     disabled — fully removed from the DOM; transcript [00:05:48]).
 *   - Destructive actions (Deactivate / Reactivate) are also hidden
 *     unless `hideDestructiveActions === false`.
 *   - All descendant `<input>` / `<select>` / `<textarea>` / MUI
 *     `<TextField>` etc. are disabled via a wrapping `<fieldset>` so
 *     consumers don't have to plumb `disabled` through every Controller.
 *
 * When `readOnly === false`:
 *   - The dialog renders identically to a plain MUI Dialog. The footer
 *     shows whatever `actions` slot the consumer passes.
 *
 * **SB2-CC-03 extension (2026-05-20).** `<fieldset disabled>` disables
 * native form controls (input / select / textarea / button) but it
 * does NOT prevent MUI's composite widgets — `<Autocomplete>`,
 * `<Switch>`, `<Slider>`, `<DatePicker>` — from opening their popovers
 * or moving their thumbs. The user can bring up the option list of a
 * Park / Type / Multi-select chip-filter and pick a new value; the
 * Save button isn't there so nothing persists, but the affordance is
 * wrong and confusing. The fix is a React context (`ReadOnlyContext`)
 * surfaced by this primitive — every wrapper around an interactive
 * MUI composite (the shared `<SiteAutocomplete>` / `<AssetModelAutocomplete>`
 * / `<TurbineAutocomplete>` / `<ComponentModelAutocomplete>` /
 * `<AssetTypeAutocomplete>` and any inline `<Autocomplete>` /
 * `<Switch>` / `<Slider>` in a consumer modal) reads
 * `useReadOnlyContext()` and applies `readOnly` (or `disabled`) to
 * the underlying MUI widget. The popover is then suppressed at the
 * MUI level, not at the DOM-attribute level.
 *
 * The matching story is `_STORIES/cross-cutting/SB2-CC-03-readonly-modal-blocks-dropdowns.md`.
 *
 * Story: `_STORIES/cross-cutting/R58-CC-02-read-only-modal-primitive.md`.
 *
 * This primitive ships alongside the `useCanEditResource` hook in
 * `@aeriosiq/ui` — together they replace the hand-rolled
 * read-only-banner / disabled-Save patterns currently in the
 * per-feature `modal-edit` folders under `apps/web/src/features`.
 */
import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  type DialogProps,
} from "@mui/material";
import { XIcon } from "@aeriosiq/ui/icons";
/**
 * SB2-CC-03 — Context published by `<ReadOnlyModal>`. Consumed by every
 * `packages/ui` and `apps/web` wrapper around an MUI composite
 * (`<Autocomplete>`, `<Switch>`, `<Slider>`, `<DatePicker>` …) so the
 * read-only state propagates through composition rather than per-prop
 * plumbing.
 *
 * The default value (`readOnly: false`) keeps existing standalone
 * usages of those wrappers — e.g. filter chips in toolbars — unaffected.
 */
interface ReadOnlyContextValue {
  readOnly: boolean;
}

const ReadOnlyContext = createContext<ReadOnlyContextValue>({
  readOnly: false,
});

/**
 * Hook for `packages/ui` primitives and `apps/web` shared form
 * components to read the modal's read-only state. Returns the
 * `readOnly` flag (defaulting to `false` when the component is rendered
 * outside any `<ReadOnlyModal>`).
 *
 * Pattern in a wrapper:
 *
 * ```ts
 * const { readOnly: ctxReadOnly } = useReadOnlyContext();
 * const readOnly = props.readOnly ?? ctxReadOnly;
 * // Apply to MUI: readOnly / disabled / open=false …
 * ```
 *
 * A consumer's explicit `readOnly` prop wins over the context — this
 * gives a wrapper an opt-out for permanently-data-readonly fields that
 * are NOT role-readonly (none in the SB2 surfaces, but the prop slot
 * is reserved).
 */
export function useReadOnlyContext(): ReadOnlyContextValue {
  return useContext(ReadOnlyContext);
}

export interface ReadOnlyModalProps
  extends Omit<DialogProps, "title" | "children"> {
  /** Whether the modal is in read-only mode. */
  readOnly: boolean;
  /**
   * Singular noun for the entity being viewed (e.g. "site", "turbine
   * model"). Used in the banner default copy
   * `"Read-only — your role does not allow editing this {entityNoun}."`.
   */
  entityNoun: string;
  /**
   * Override the default banner copy. Use this when the read-only
   * reason is NOT "your role" — e.g. when an inactive resource is
   * read-only regardless of role.
   */
  bannerOverride?: string;
  /**
   * Modal title (replaces MUI's plain string `title` prop). Renders
   * inside `DialogTitle`.
   */
  title: ReactNode;
  /** The modal body. */
  children: ReactNode;
  /**
   * Footer actions slot. Rendered inside `DialogActions`. The consumer
   * is responsible for not rendering Save / Submit / Deactivate when
   * `readOnly` is true — this primitive enforces the omission by NOT
   * RENDERING this slot at all when `readOnly` AND
   * `hideDestructiveActions` (the default) is on.
   *
   * Tip: pass a single Close button when in read-only mode; the
   * primitive then renders that single button.
   */
  actions?: ReactNode;
  /**
   * Even when `readOnly === true`, render the `actions` slot. Use this
   * when the actions are non-destructive (e.g. just a Close button) and
   * you want them visible in read-only mode. Default: `false` —
   * actions are hidden in read-only mode.
   */
  showActionsInReadOnly?: boolean;
  /**
   * When false, render the destructive actions row (Deactivate /
   * Reactivate) even in `readOnly` mode. Default: `true` — destructive
   * actions are hidden.
   */
  hideDestructiveActions?: boolean;
  /**
   * Optional onClose callback for the dialog's top-right close button.
   * When set, an `aria-label="Close"` IconButton renders in the title.
   */
  onClose?: () => void;
  /**
   * Test hook for the banner. Defaults to
   * `"read-only-modal-banner"`.
   */
  bannerTestId?: string;
}

/**
 * Default banner copy template. Per Christian's UX brief §6
 * (transcript [00:05:24]) the banner is full-width, sticky at the top,
 * and reads:
 *
 *   "Read-only — your role does not allow editing this <entityNoun>."
 */
function defaultBannerCopy(entityNoun: string): string {
  return `Read-only — your role does not allow editing this ${entityNoun}.`;
}

export function ReadOnlyModal({
  readOnly,
  entityNoun,
  bannerOverride,
  title,
  children,
  actions,
  showActionsInReadOnly = false,
  hideDestructiveActions: _hideDestructiveActions = true,
  onClose,
  bannerTestId = "read-only-modal-banner",
  ...dialogProps
}: ReadOnlyModalProps): ReactElement {
  const bannerCopy = bannerOverride ?? defaultBannerCopy(entityNoun);

  // The actions row is suppressed when read-only AND showActionsInReadOnly
  // is false. Consumers that want a Close button in read-only mode set
  // `showActionsInReadOnly` to true (e.g. for accessibility's sake).
  const renderActions = !readOnly || showActionsInReadOnly;

  return (
    // NOTE: do NOT set `role="dialog"` on the outer `<Dialog>` — MUI's
    // internal `DialogPaper` already carries `role="dialog"` (verified in
    // `@mui/material/Dialog/Dialog.js`). Setting it on the outer Modal
    // root too creates two `role="dialog"` nodes, which trips Playwright
    // strict-mode `getByRole('dialog')` queries. The accessible name is
    // resolved by MUI via `aria-labelledby` auto-wired to `<DialogTitle>`.
    <Dialog {...(onClose ? { onClose } : {})} {...dialogProps}>
      <ReadOnlyContext.Provider value={{ readOnly }}>
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
          {onClose ? (
            <IconButton
              aria-label="Close"
              onClick={onClose}
              size="small"
              edge="end"
            >
              <XIcon fontSize="small" />
            </IconButton>
          ) : null}
        </DialogTitle>

        {/* Read-only banner — sticky at the top of the dialog body so a
            long form doesn't scroll the banner out of view. */}
        {readOnly ? (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              px: 3,
              pt: 1,
              pb: 0,
              bgcolor: "background.paper",
            }}
          >
            <Alert
              severity="info"
              variant="outlined"
              role="status"
              aria-live="polite"
              data-testid={bannerTestId}
            >
              {bannerCopy}
            </Alert>
          </Box>
        ) : null}

        {/* In read-only mode the `<fieldset disabled>` wrap below strips
            every focusable element from the scrollable DialogContent. axe-
            core's `scrollable-region-focusable` rule (WCAG 2.1.1 / 2.1.3)
            then flags `.MuiDialogContent-root` as a serious violation —
            the scrollable region has no focusable content and is itself
            not focusable, so keyboard users can't scroll it. Adding
            `tabIndex={0}` to the DialogContent gives keyboard users a
            focus stop that the browser will treat as the scroll target.
            In editable mode the form's own inputs satisfy axe, so the
            extra tab stop is unnecessary and only adds focus-order noise. */}
        <DialogContent {...(readOnly ? { tabIndex: 0 } : {})}>
          {/* When read-only, wrap descendants in a disabled fieldset so
              every descendant Input / Select / Textarea is disabled
              without per-field plumbing. Reset `border` / `padding` /
              `min-width` so the fieldset is visually invisible.
              Per HTML5 spec, a disabled fieldset transitively disables
              all descendant form controls. */}
          {readOnly ? (
            <Box
              component="fieldset"
              disabled
              sx={{
                border: "none",
                padding: 0,
                margin: 0,
                minInlineSize: 0,
                // Restore the natural block layout. <fieldset> has its
                // own intrinsic layout quirks; reset display so consumers
                // can use their own Stack/Box without conflict.
                display: "block",
              }}
            >
              {children}
            </Box>
          ) : (
            children
          )}
        </DialogContent>

        {renderActions && actions ? (
          <DialogActions>{actions}</DialogActions>
        ) : null}
      </ReadOnlyContext.Provider>
    </Dialog>
  );
}
